<script setup lang="ts">
import { type Money, requireCurrencyCode } from '@taler/domain'
import { ActionButton, Card, StatusTag } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import AmountInput from '@/app/components/AmountInput.vue'
import { AccountPicker, type Target } from '@/features/entries'
import type { SourceAccountOption } from '../internal/source-accounts.ts'
import type { Loan, RecordLoanDrawdownInput } from '../internal/types.ts'

const props = defineProps<{
  loan: Loan
  accounts: readonly SourceAccountOption[]
  isSubmitting: boolean
  errorMessage: string | undefined
}>()
const emit = defineEmits<{ record: [input: RecordLoanDrawdownInput] }>()

const { t, locale } = useI18n()
const currentLocale = computed(() => locale.value as 'en' | 'hu')
const loanCurrency = computed(() => requireCurrencyCode(props.loan.currency))
const amount = ref<Money | null>(null)
const destinationAccountId = ref('')

function submit(): void {
  if (destinationAccountId.value === '' || amount.value === null) return
  emit('record', { amount: amount.value, destinationAccountId: destinationAccountId.value })
}

watch(
  () => props.isSubmitting,
  (submitting, wasSubmitting) => {
    if (wasSubmitting && !submitting && props.errorMessage === undefined) {
      amount.value = null
      destinationAccountId.value = ''
    }
  },
)

const allowedIds = computed(() => new Set(props.accounts.map((account) => account.id)))

function unavailableAccount(target: Target): string | undefined {
  return allowedIds.value.has(target.id) ? undefined : t('entries.pickerNotAvailable')
}
</script>

<template>
  <Card>
    <form class="flex flex-col items-start gap-3" @submit.prevent="submit">
      <h3 class="text-[length:var(--text-step-3)] font-semibold text-[color:var(--color-text)]">
        {{ t('loans.recordDrawdown') }}
      </h3>
      <p class="text-[color:var(--color-text-muted)]">{{ t('loans.drawdownExplanation') }}</p>

      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('loans.drawdownAmount')
        }}</span>
        <AmountInput v-model="amount" :currency="loanCurrency" :locale="currentLocale" required />
      </label>

      <label class="flex w-full flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('loans.drawdownAccount')
        }}</span>
        <AccountPicker
          id="loan-drawdown-account"
          :label="t('loans.drawdownAccount')"
          :model-value="destinationAccountId === '' ? undefined : destinationAccountId"
          :currency="loan.currency"
          :accounts-label="t('entries.pickerTransferAccounts')"
          :unavailable="unavailableAccount"
          @update:model-value="(value) => (destinationAccountId = value ?? '')"
        />
      </label>

      <StatusTag v-if="errorMessage" tone="danger">{{ errorMessage }}</StatusTag>

      <ActionButton type="submit" :disabled="isSubmitting">
        {{ isSubmitting ? t('loans.recording') : t('loans.recordDrawdown') }}
      </ActionButton>
    </form>
  </Card>
</template>
